import { ChangeDetectionStrategy, Component, computed, inject, input, ViewEncapsulation } from "@angular/core";
import { DataIngestionService, IngestionPoint, IngestionWorkflowAuditRow } from "./data.ingestion.service";
import { INGESTION_SECURITY_SERVICE } from "./ingestion.security.service";
import { UserStore } from "../../stores/user.store";
import { s25Resource } from "../../util/s25.resource";
import { S25Util } from "../../util/s25-util";
import { S25Datefilter } from "../s25-dateformat/s25.datefilter.service";
import { S25LoadingInlineModule } from "../s25-loading/s25.loading.inline.module";
import { S25TableModule } from "../s25-table/s25.table.module";
import { Table } from "../s25-table/Table";

@Component({
    selector: "s25-ng-ingestion-workflow-audit",
    template: `
        @if (canViewAudit()) {
            <div class="workflow-audit-section c-margin-top--double c-margin-bottom--double">
                <h3 class="c-margin-bottom--single">Workflow Audit History</h3>
                @if (auditResource.resource.error()) {
                    <div class="alert alert-warning" role="alert">
                        Could not load workflow audit (you may not have permission to view it).
                    </div>
                } @else if (auditResource.resource.isLoading()) {
                    <s25-ng-loading-inline-static></s25-ng-loading-inline-static>
                } @else {
                    <s25-ng-table
                        [caption]="'Workflow audit history'"
                        [unlimitedWidth]="true"
                        [columnSortable]="true"
                        [dataSource]="auditTableConfig"
                        [hasExport]="true"
                        [hasRefresh]="true"
                        [exportFilename]="'form-workflow-audit.xlsx'"
                        [exportSheetName]="'Workflow audit history'"
                        [showHeaderWhenNoData]="true"
                    />
                }
            </div>
        }
    `,
    styles: `
        .workflow-audit-section h3 {
            font-size: 1.1rem;
        }
        :host ::ng-deep .expanded-content-wrapper {
            max-height: min(70vh, 720px);
            overflow: auto;
        }
        :host ::ng-deep .audit-details {
            max-width: 280px;
            white-space: pre-wrap;
            word-break: break-word;
        }
        :host ::ng-deep .workflow-audit-type {
            word-break: break-all;
        }
    `,
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.Emulated,
    imports: [S25LoadingInlineModule, S25TableModule],
})
export class IngestionWorkflowAuditComponent {
    readonly submissionIdUuid = input<string | undefined>();
    readonly ingestionPoint = input<IngestionPoint | undefined>();

    private readonly security = inject(INGESTION_SECURITY_SERVICE, { optional: true });
    private readonly userStore = inject(UserStore);

    readonly canViewAudit = computed(() => {
        const ingestionPoint = this.ingestionPoint();
        const submissionIdUuid = this.submissionIdUuid();
        if (!ingestionPoint?.ingestionPointId || !submissionIdUuid) {
            return false;
        }
        return this.security?.canManageForm(ingestionPoint) ?? false;
    });

    readonly auditResource = s25Resource({
        if: () => this.canViewAudit() && !!this.submissionIdUuid(),
        params: () => this.submissionIdUuid()!,
        loader: async ({ params: uuid }) => DataIngestionService.getWorkflowAuditHistory(uuid),
    });

    readonly auditRows = computed<IngestionWorkflowAuditRow[]>(() => this.auditResource.value() ?? []);

    private readonly loadAuditTableRows = async (): Promise<Table.DataSourceResponse> => {
        const rows: Table.Row[] = [];
        for (const item of this.auditRows()) {
            rows.push({
                id: item.workflowAuditId?.toString() ?? "",
                name: `${item.auditType ?? ""} — ${item.workflowAuditId ?? ""}`,
                cells: {
                    auditDate: {
                        text: this.formatWhen(item.auditDate),
                        sortValue: item.auditDate || "",
                    },
                    organizationDisplayName: { text: item.organizationDisplayName || "—" },
                    auditUser: { text: item.auditUser || "" },
                    auditType: { text: item.auditType || "", className: "workflow-audit-type" },
                    workflowProcessDisplayName: { text: item.workflowProcessDisplayName || "—" },
                    stageDisplayName: { text: item.stageDisplayName || this.stageFallback(item) },
                    stateDisplayName: { text: item.stateDisplayName || this.stateFallback(item) },
                    description: { text: item.description || "—" },
                    auditDetails: { text: this.detailText(item), className: "audit-details" },
                },
                data: item,
            });
        }
        return { rows };
    };

    readonly auditTableConfig: Table.DataSource = {
        type: "unpaginated",
        columns: [
            { id: "auditDate", header: "When", export: true },
            { id: "organizationDisplayName", header: "Organization", export: true },
            { id: "auditUser", header: "Who", export: true },
            { id: "auditType", header: "Type", export: true },
            { id: "workflowProcessDisplayName", header: "Process", export: true },
            { id: "stageDisplayName", header: "Stage", export: true },
            { id: "stateDisplayName", header: "State", export: true },
            { id: "description", header: "Summary", export: true },
            { id: "auditDetails", header: "Details", export: true },
        ],
        dataSource: this.loadAuditTableRows,
    };

    formatWhen(iso: string | undefined): string {
        if (!iso) {
            return "—";
        }
        const fmt = this.userStore.dateTimeFormat();
        if (!fmt) {
            return iso;
        }
        return S25Datefilter.transform(S25Util.date.parse(iso), fmt) || "—";
    }

    stageFallback(row: IngestionWorkflowAuditRow): string {
        return row.stageId != null ? String(row.stageId) : "—";
    }

    stateFallback(row: IngestionWorkflowAuditRow): string {
        return row.stateId != null ? String(row.stateId) : "—";
    }

    detailText(row: IngestionWorkflowAuditRow): string {
        const parts: string[] = [];
        if (row.auditType === "STAGE_TRANSITION" && row.previousValue) {
            parts.push(`From stage: ${row.previousValue}`);
        }
        if (row.newValue) {
            parts.push(row.newValue);
        }
        return parts.length ? parts.join("\n") : "—";
    }
}
